import { useState } from "react";
import { AlertColor, Box, IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import editIcon from "../../assets/images/edit_icon.png";
import splashMockImage from "../../assets/images/welcomeVW.png";

import BoxContent from "../../components/boxContent";
import DataGridApi from "../../components/dataGridApi";
import QuickAlert from "../../components/form/quickAlert";
import MediaPreviewCell from "./components/MediaPreviewCell";
import ModalAddSplashScreen from "./modalAddSplashScreen";
import ModalEditSplashScreen from "./modalEditSplashScreen";

export default function SplashScreen() {
  const { t } = useTranslation();

  const [dataUpdated, setDataUpdated] = useState(0);
  const [openModalAdd, setOpenModalAdd] = useState(false);
  const [itemToEdit, setItemToEdit] = useState<any>(null);
  const [visibleAlert, setVisibleAlert] = useState<boolean>(false);
  const [severityAlert, setSeverityAlert] = useState<AlertColor>("success");
  const [messageQuickAlert, setMessageQuickAlert] = useState("");
  const [titleMessage, setTitleMessage] = useState("");

  const refreshPage = () => {
    setDataUpdated((prev) => prev + 1);
  };

  const openSuccessAlert = (message?: string) => {
    setVisibleAlert(true);
    setSeverityAlert("success");
    setMessageQuickAlert(message || t("splashScreen.alert.successMessage"));
    setTitleMessage(t("quickAlert.sucess"));
  };

  const openErrorAlert = (errorMessage?: string) => {
    setVisibleAlert(true);
    setSeverityAlert("error");
    setMessageQuickAlert(errorMessage ?? t("splashScreen.alert.errorMessage"));
    setTitleMessage(t("quickAlert.error2"));
  };

  const columns = [
    {
      field: "name",
      headerName: t("splashScreen.columns.title"),
      flex: 1,
      minWidth: 200,
      headerClassName: "header-style",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: any) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          {params.row?.name || params.row?.title || "-"}
        </Box>
      ),
    },
    {
      field: "startDate",
      headerName: t("splashScreen.columns.startDate"),
      flex: 0.8,
      minWidth: 130,
      headerClassName: "header-style",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: any) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          {params.row?.startDate
            ? dayjs(params.row.startDate).format("DD/MM/YYYY")
            : "-"}
        </Box>
      ),
    },
    {
      field: "endDate",
      headerName: t("splashScreen.columns.endDate"),
      flex: 0.8,
      minWidth: 130,
      headerClassName: "header-style",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: any) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          {params.row?.endDate
            ? dayjs(params.row.endDate).format("DD/MM/YYYY")
            : "-"}
        </Box>
      ),
    },
    {
      field: "status",
      headerName: t("splashScreen.columns.status"),
      flex: 0.7,
      minWidth: 120,
      headerClassName: "header-style",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: any) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          {params.row?.status
            ? String(
                t(
                  `splashScreen.status.${params.row.status}`,
                  params.row.status,
                ),
              )
            : "-"}
        </Box>
      ),
    },
    {
      field: "media",
      headerName: t("splashScreen.columns.media"),
      flex: 1,
      minWidth: 150,
      headerClassName: "header-style",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: any) => {
        const mediaType = params.row?.mediaType;
        const mediaUrl =
          mediaType === "IMAGE"
            ? params.row?.fileUrlMdpi ||
              params.row?.fileUrlHdpi ||
              params.row?.fileUrlXhdpi ||
              params.row?.fileUrl
            : params.row?.fileUrl || params.row?.media;

        return (
          <MediaPreviewCell
            mediaType={mediaType}
            mediaUrl={mediaUrl}
            ariaLabel={t("splashScreen.columns.media")}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: t("splashScreen.columns.actions"),
      flex: 0.7,
      minWidth: 110,
      headerClassName: "header-style",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: any) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setItemToEdit(params.row);
            }}
          >
            <img
              src={editIcon}
              alt={t("splashScreen.actions.edit")}
              width={22}
              height={20}
            />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <BoxContent
      title={t("splashScreen.list.title")}
      button
      titleButton={t("splashScreen.list.addButtonText")}
      handleClick={() => setOpenModalAdd(true)}
    >
      <Box
        sx={{
          marginBottom: 3,
        }}
      >
        <span style={{ fontSize: 15, fontWeight: 400 }}>
          {t("splashScreen.list.description")}
        </span>
      </Box>

      <Box
        sx={{
          marginBottom: 2,
        }}
      >
        <span style={{ fontSize: 12 }}>
          {t("splashScreen.list.searchDescription")}
        </span>
      </Box>

      <DataGridApi
        onRowClick={() => {}}
        mock={[]}
        columnsData={columns}
        endPoint="splash/contents?"
        labelParams1={t("splashScreen.list.searchInputText")}
        globalSearchEnabled
        globalSearchParam="&searchText="
        filterInputWidth={270}
        boldHeaderTitle
        cleanButtonText={t("splashScreen.list.cleanButtonText")}
        cleanButtonType="outlined"
        dataUpdated={dataUpdated}
        onEmptyMessageApi={t("splashScreen.list.emptyMessage")}
      />

      <QuickAlert
        visibility={visibleAlert}
        description={messageQuickAlert}
        severity={severityAlert}
        title={titleMessage}
        hanhleClose={() => setVisibleAlert(false)}
      />

      {openModalAdd && (
        <ModalAddSplashScreen
          onClose={() => setOpenModalAdd(false)}
          openSuccessAlert={openSuccessAlert}
          openErrorAlert={openErrorAlert}
          refreshPage={refreshPage}
        />
      )}

      {itemToEdit && (
        <ModalEditSplashScreen
          data={itemToEdit}
          onClose={() => setItemToEdit(null)}
          openSuccessAlert={openSuccessAlert}
          openErrorAlert={openErrorAlert}
          refreshPage={refreshPage}
        />
      )}
    </BoxContent>
  );
}
